import React from 'react';
import { UserStatusBadges, RoleBadges, UserActionButtons } from '@molecules';
import type { UserRole } from '@molecules';

export interface User {
  id: string;
  email: string;
  username: string;
  name: string | null;
  isAdmin: boolean;
  isActive: boolean;
  azureId: string | null;
  createdAt: string;
  updatedAt: string;
  roles: UserRole[];
}

export interface UserTableProps {
  /** Array of users to display */
  users: User[];
  /** Callback when edit button is clicked */
  onEditUser: (user: User) => void;
  /** Callback when delete button is clicked */
  onDeleteUser: (user: User) => void;
  /** ID of user currently being deleted (for loading state) */
  deletingUserId?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * UserTable Organism Component
 * 
 * A complete table for displaying users with their information and actions.
 * 
 * @example
 * ```tsx
 * <UserTable
 *   users={filteredUsers}
 *   onEditUser={handleEditUser}
 *   onDeleteUser={handleDeleteUser}
 *   deletingUserId={deletingUserId}
 * />
 * ```
 */
export const UserTable: React.FC<UserTableProps> = ({
  users,
  onEditUser,
  onDeleteUser,
  deletingUserId,
  className = '',
}) => {
  if (users.length === 0) {
    return (
      <div className={`bg-background-primary rounded-lg border border-border-primary overflow-hidden ${className}`}>
        <div className="px-6 py-8 text-center text-text-secondary">
          No users found.
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-background-primary rounded-lg border border-border-primary overflow-hidden ${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-background-secondary">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Roles
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-background-secondary">
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <div className="text-sm font-medium text-text-primary">
                      {user.name || user.username}
                    </div>
                    <div className="text-sm text-text-secondary">
                      {user.email}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <UserStatusBadges 
                    isActive={user.isActive} 
                    isAdmin={user.isAdmin} 
                  />
                </td>
                <td className="px-6 py-4">
                  <RoleBadges roles={user.roles} />
                </td>
                <td className="px-6 py-4 text-sm text-text-secondary">
                  {new Date(user.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-right">
                  <UserActionButtons
                    onEdit={() => onEditUser(user)}
                    onDelete={() => onDeleteUser(user)}
                    isDeleting={deletingUserId === user.id}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
